import { useEffect, useState } from "react";
import {
  CheckCircle2, CalendarDays, Wallet, Video, Clock, ArrowRight, Syringe, Stethoscope, BadgeCheck,
} from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import LpLeadForm from "@/pages/lp/LpLeadForm";
import AtlasPromo from "@/components/site/AtlasPromo";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { injectJsonLd, removeJsonLd } from "@/lib/seo";

/**
 * Google-Ads-LP für die Top-Kampagne „Suchnetzwerk LG1 & LG2“.
 * Keywords: „lg1 lg2 weiterbildung“, „lg1 lg2 online kurs“, „behandlungspflege lg1 lg2“,
 * „lg1 und lg2 kurse in der nähe“, „wo kann ich lg1 und lg2 machen“.
 * Final-URL: /lg1-lg2 (ersetzt die alte WordPress-Seite 1:1).
 */
const USPS = [
  { icon: CalendarDays, text: "Start jeden Montag – keine Wartezeit" },
  { icon: Video, text: "100 % online live – LG1 & LG2 von überall" },
  { icon: Wallet, text: "Bildungsgutschein: meist 100 % gefördert" },
  { icon: BadgeCheck, text: "Anerkannt nach Landesrahmenvertrag NRW" },
];

const FAQ = [
  ["Kann ich LG1 und LG2 wirklich online machen?",
   "Ja – der komplette Theorieunterricht läuft 100 % online live per Zoom mit echten Dozent:innen. Du brauchst also keinen Kurs „in der Nähe“: Du machst LG1 & LG2 bequem von zu Hause, egal wo du wohnst."],
  ["Welche Voraussetzungen brauche ich?",
   "Für die Pflegeassistenz nach LG1 & LG2 brauchst du eine Pflege-Grundqualifikation oder Berufserfahrung in der Pflege. Ohne Vorerfahrung? Kein Problem – dann ist die Ambulante Pflegeassistenz dein Einstieg, dort sind die LG1/LG2-Grundlagen bereits enthalten."],
  ["Was darf ich nach der Weiterbildung?",
   "Mit LG1 darfst du z. B. Medikamente verabreichen, Blutdruck & Blutzucker messen und subkutane Injektionen geben. Mit LG2 kommen Wundversorgung (Dekubitus bis Grad 2), Katheter- und Sondenpflege (SPK, PEG), Ausscheidungshilfe, Flüssigkeitsbilanzierung und spezielle lokale Therapien dazu – Leistungen, die dein Arbeitgeber direkt abrechnen kann. Das macht dich sofort wertvoller im Team."],
  ["Wie läuft die Förderung über den Bildungsgutschein?",
   "Mit einem Bildungsgutschein der Agentur für Arbeit oder des Jobcenters ist die Weiterbildung für dich meist komplett kostenlos. Wir beraten dich kostenlos zur Beantragung und übernehmen den Papierkram."],
];

export default function LpLg1Lg2() {
  useLpMeta(
    "LG1 & LG2 Weiterbildung · 100 % online · Start jeden Montag | Dickies-Campus",
    "Behandlungspflege LG1 & LG2 online machen: Start jeden Montag, 6,5 Wochen, anerkannt nach Rahmenvertrag NRW. Mit Bildungsgutschein meist kostenlos."
  );
  const [bookingCourse, setBookingCourse] = useState(null);
  const lg = COURSE_LIST.find((c) => c.id === "pflegeassistent-lg1-lg2");
  const amb = COURSE_LIST.find((c) => c.id === "ambulanter-pflegeassistent");

  // FAQ-Rich-Snippet für Google (beantwortet die Top-Suchanfragen direkt im Suchergebnis)
  useEffect(() => {
    injectJsonLd("ld-faq-lg1lg2", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ.map(([q, a]) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
      })),
    });
    return () => removeJsonLd("ld-faq-lg1lg2");
  }, []);

  return (
    <LpShell>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20">
        <div className="max-w-3xl">
          <GoogleStarsBadge />
          <h1 data-testid="lp-lg-headline" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-sand-800 leading-[1.05]">
            LG1 & LG2 Weiterbildung.<br />
            <span className="text-terra">100 % online. Start jeden Montag.</span>
          </h1>
          <p className="mt-5 text-sand-600 text-base md:text-lg leading-relaxed max-w-xl">
            Qualifiziere dich für die <strong className="text-sand-800">Behandlungspflege nach LG1 & LG2</strong> –
            in 6,5 Wochen, online live von zu Hause, mit Bildungsgutschein meist{" "}
            <strong className="text-sand-800">komplett kostenlos</strong>.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {USPS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5 text-[14px] text-sand-700">
                <Icon className="h-5 w-5 text-terra shrink-0" strokeWidth={1.7} />
                {text}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#lp-kurse" data-testid="lp-lg-cta-courses"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 py-3.5 text-base font-semibold transition-colors group">
              Kurs & Preise ansehen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
            </a>
            <a href="#lp-kontakt" data-testid="lp-lg-cta-beratung"
              className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 py-3.5 text-base font-medium transition-colors">
              Kostenlose Beratung
            </a>
          </div>
        </div>
      </section>

      {/* Was darfst du mit LG1 / LG2 */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">Das darfst du nach der Weiterbildung</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl border border-cream-300 p-7" data-testid="lp-lg1-card">
              <span className="inline-flex items-center gap-2 rounded-full bg-terra/10 text-terra px-4 h-8 text-[12px] font-bold uppercase tracking-[0.18em]">
                <Stethoscope className="h-3.5 w-3.5" strokeWidth={2} /> LG1 · Behandlungspflege Stufe 1
              </span>
              <ul className="mt-5 space-y-2.5 text-[14.5px] text-sand-700">
                {["Medikamentengabe (Richten & Verabreichen)", "Blutzuckermessung inkl. Protokollierung", "Subkutane Injektionen (z. B. Insulin, Thrombosespritzen)", "Blutdruckmessung & Dokumentation", "Kompressionsstrümpfe ab Klasse 1 / Kompressionsverbände"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-[18px] w-[18px] text-terra shrink-0 mt-0.5" strokeWidth={1.8} />{t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl border border-cream-300 p-7" data-testid="lp-lg2-card">
              <span className="inline-flex items-center gap-2 rounded-full bg-terra/10 text-terra px-4 h-8 text-[12px] font-bold uppercase tracking-[0.18em]">
                <Syringe className="h-3.5 w-3.5" strokeWidth={2} /> LG2 · Behandlungspflege Stufe 2
              </span>
              <ul className="mt-5 space-y-2.5 text-[14.5px] text-sand-700">
                {["Wundversorgung (Dekubitus bis Grad 2)", "Katheter- & Sondenpflege (SPK, PEG inkl. Nahrungsgabe)", "Ausscheidungshilfe (Klysmen, Klistiere, Einläufe)", "Flüssigkeitsbilanzierung", "Spezielle lokale Therapien (Einreibungen, dermatologische Bäder)"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-[18px] w-[18px] text-terra shrink-0 mt-0.5" strokeWidth={1.8} />{t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-6 text-[13.5px] text-sand-600 max-w-2xl">
            Heißt für dich: <strong className="text-sand-800">mehr Verantwortung, abrechenbare Leistungen, besseres Gehalt</strong> –
            und ein Arbeitgeber, der dich nicht mehr hergeben will.
          </p>
        </div>
      </section>

      {/* Zwei Wege */}
      <section id="lp-kurse" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-3xl text-sand-800 mb-3">Dein Weg zu LG1 & LG2</h2>
        <p className="text-sand-600 text-[14.5px] mb-8 max-w-2xl">
          Egal ob du schon in der Pflege arbeitest oder neu einsteigst – wir haben den passenden Kurs.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { c: lg, badge: "Mit Pflegeerfahrung", note: "Voraussetzung: Pflege-Grundqualifikation oder Berufserfahrung" },
            { c: amb, badge: "Ohne Vorerfahrung / Quereinstieg", note: "LG1/LG2-Grundlagen inklusive – dein Einstieg in die Pflege" },
          ].map(({ c, badge, note }) => c && (
            <div key={c.id} data-testid={`lp-lg-course-${c.id}`}
              className="bg-white border border-cream-300 rounded-3xl p-7 flex flex-col shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all">
              <span className="self-start rounded-full bg-sand-800 text-cream-50 px-4 h-7 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.16em]">
                {badge}
              </span>
              <h3 className="mt-4 font-display text-[1.35rem] text-sand-800 leading-snug">{c.title}</h3>
              <p className="text-[13px] text-sand-600 mt-1.5">{c.subtitle}</p>
              <div className="mt-4 space-y-1.5 text-[13px] text-sand-700">
                <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {c.weeks} · {c.duration}</div>
                <div className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> Start: {c.nextStart}</div>
                <div className="flex items-center gap-2"><Wallet className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {c.funding}</div>
              </div>
              <p className="mt-3 text-[12.5px] text-sand-500">{note}</p>
              <div className="mt-5 pt-4 border-t border-cream-200 flex items-center justify-between">
                <span className="font-display text-xl font-semibold text-sand-800">{c.price}</span>
                <button type="button" data-testid={`lp-lg-book-${c.id}`} onClick={() => setBookingCourse(c)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-terra hover:bg-terra-dark text-white px-5 py-2.5 text-[13px] font-semibold transition-colors">
                  Jetzt buchen <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Atlas-Bridge · Bonus-Asset: "Was darfst du nach LG1/LG2 wirklich?" */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AtlasPromo variant="ads" testId="lp-lg-atlas-promo" />
      </section>

      {/* FAQ – beantwortet die Top-Suchanfragen */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">Häufige Fragen zu LG1 & LG2</h2>
          <div className="space-y-4">
            {FAQ.map(([q, a]) => (
              <div key={q} className="bg-white rounded-3xl border border-cream-300 p-6">
                <h3 className="font-semibold text-sand-800 text-[15.5px]">{q}</h3>
                <p className="mt-2 text-[14px] text-sand-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section id="lp-kontakt" className="bg-brand-rose py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-brand-darkred leading-tight">
              Unsicher, welcher Weg passt?<br /><span className="text-white">Wir beraten dich kostenlos.</span>
            </h2>
            <p className="mt-4 text-brand-darkred/80 text-[15px] leading-relaxed max-w-md">
              Über 1.000 Pflegekräfte haben wir bereits qualifiziert. Wir prüfen mit dir,
              ob du den Bildungsgutschein bekommst – und übernehmen den Papierkram.
            </p>
            <TrackedPhoneLink source="lp-lg1-lg2" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-brand-darkred hover:text-white transition-colors">
              📞 0211 29273330
            </TrackedPhoneLink>
          </div>
          <LpLeadForm source="ads-lp-lg1-lg2" audience="pflegekraft"
            sub="Sag uns kurz, wo du gerade stehst – wir melden uns mit einer ehrlichen Empfehlung für deinen LG1/LG2-Weg." />
        </div>
      </section>

      <BookingDialog course={bookingCourse} open={!!bookingCourse} onOpenChange={(v) => !v && setBookingCourse(null)}
        initialBuyerType="selbstzahler" />
    </LpShell>
  );
}
